/**
 * Notes API Service
 *
 * Functions for interacting with the Notes API endpoints.
 */

import api from "./api";
import type {
  NoteApiResponse,
  NoteListApiResponse,
  NotePublicListApiResponse,
  NoteReviewRequest,
} from "../types/api";

export function createIdempotencyKey(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Get a single note by ID.
 *
 * @param noteId - UUID of the note
 * @returns Note data including status and intent classification
 */
export async function getNote(noteId: string): Promise<NoteApiResponse> {
  const response = await api.get<NoteApiResponse>(`/notes/${noteId}`);
  return response.data;
}

/**
 * List notes with pagination.
 *
 * @param page - Page number (1-indexed)
 * @param pageSize - Number of items per page
 * @returns Paginated note list
 */
export async function listNotes(
  page: number = 1,
  pageSize: number = 20,
): Promise<NoteListApiResponse | NotePublicListApiResponse> {
  // Backend returns NoteListApiResponse (full) when Bearer token is present,
  // NotePublicListApiResponse (truncated) when anonymous. api.ts only attaches
  // the token when authToken is set, so the same call works for both modes.
  const response = await api.get<
    NoteListApiResponse | NotePublicListApiResponse
  >("/notes", {
    params: { page, page_size: pageSize },
  });
  return response.data;
}

/**
 * Create a new note from text.
 *
 * @param contentText - The note content
 * @param tags - Optional tags
 * @returns Created note
 */
export async function createNote(
  contentText: string,
  tags: string[],
  idempotencyKey: string,
): Promise<NoteApiResponse> {
  const response = await api.post<NoteApiResponse>("/notes", {
    content_text: contentText,
    tags,
    input_type: "TEXT",
    idempotency_key: idempotencyKey,
  });
  return response.data;
}

/**
 * Create a note from a URL.
 *
 * @param sourceUrl - The URL to extract content from
 * @returns Created note
 */
export async function createNoteFromLink(
  sourceUrl: string,
): Promise<NoteApiResponse> {
  const response = await api.post<NoteApiResponse>("/notes/from-link", {
    source_url: sourceUrl,
  });
  return response.data;
}

/**
 * Review an LLM draft: accept, edit, or discard.
 *
 * @param noteId - UUID of the note in REVIEW status
 * @param review - Review action and optional edits
 * @returns Updated note
 */
export async function reviewNote(
  noteId: string,
  review: NoteReviewRequest,
): Promise<NoteApiResponse> {
  const response = await api.post<NoteApiResponse>(
    `/notes/${noteId}/review`,
    review,
  );
  return response.data;
}

/**
 * Delete a note by ID.
 *
 * @param noteId - UUID of the note
 */
export async function deleteNote(noteId: string): Promise<void> {
  await api.delete(`/notes/${noteId}`);
}
